import React from 'react';
import { LinkedInPreview } from './previews/LinkedInPreview';
import { InstagramPreview } from './previews/InstagramPreview';
import { FacebookPreview } from './previews/FacebookPreview';
import { TwitterPreview } from './previews/TwitterPreview';
import { YouTubePreview } from './previews/YouTubePreview';
import { GoogleBusinessPreview } from './previews/GoogleBusinessPreview';
import { TikTokPreview } from './previews/TitkokPreview';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { Brand } from '../../types';
import type { Post } from '../../types/calendar';
import type { PlatformContent } from '../../services/contentService';

interface SocialMediaPostPreviewProps {
  platform: SocialPlatform;
  content: string | PlatformContent | object;
  brand?: Brand;
  brandName?: string;
  brandLogo?: string;
  media?: Post['media'];
  format?: 'regular' | 'reel';
}

export function SocialMediaPostPreview({
  platform,
  content,
  brand,
  brandName,
  brandLogo,
  media,
  format = 'regular'
}: SocialMediaPostPreviewProps) {
  const mediaUrl = media?.url;
  
  // Use either brand object or individual props
  const displayBrandName = brandName || brand?.name;
  const displayBrandLogo = brandLogo || brand?.logo;
  
  // Ensure content is a string
  let contentString = '';
  
  if (typeof content === 'string') {
    // Preserve newlines in string content and handle escaped newlines
    contentString = content.replace(/\\n/g, '\n'); // Convert escaped newlines to real newlines
  } else if (content && typeof content === 'object') {
    // Check if it has a text property (PlatformContent)
    if ('text' in content && content.text) {
      contentString = typeof content.text === 'string' 
        ? content.text.replace(/\\n/g, '\n') // Convert escaped newlines in content.text
        : JSON.stringify(content.text);
    } else {
      // Otherwise stringify the whole object
      contentString = JSON.stringify(content);
    }
  } else {
    contentString = String(content);
  }

  // Make sure contentString maintains any newline characters

  // Split platform previews into a separate function for clarity
  return renderPlatformPreview(platform, contentString, brand, displayBrandName, displayBrandLogo, mediaUrl, format);
}

// Helper function to render the appropriate platform preview
function renderPlatformPreview(
  platform: SocialPlatform, 
  content: string, 
  brand?: Brand, 
  brandName?: string, 
  brandLogo?: string, 
  mediaUrl?: string, 
  format: 'regular' | 'reel' = 'regular'
) {
  // Process the content one more time to ensure we replace any escaped newlines
  const processedContent = content.replace(/\\n/g, '\n');
  
  // Check platform and render appropriate preview
  switch (platform) {
    case 'linkedin':
      return <LinkedInPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'instagram':
      return <InstagramPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'facebook':
      return <FacebookPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'twitter':
      return <TwitterPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'youtube':
      return <YouTubePreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'google-business':
      return <GoogleBusinessPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'tiktok':
      return <TikTokPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    default:
      return <LinkedInPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
  }
}

// Helper to get content string for each platform
export const getContentString = (
  content: string | { platforms: Record<string, string | PlatformContent> } | any,
  platform: string
): string => {
  if (typeof content === 'string') {
    return content;
  }
  
  if (content && typeof content === 'object' && 'platforms' in content) {
    const platformContent = content.platforms[platform];
    
    if (typeof platformContent === 'string') {
      return platformContent;
    }
    
    if (platformContent && typeof platformContent === 'object' && 'text' in platformContent) {
      return typeof platformContent.text === 'string' 
        ? platformContent.text 
        : JSON.stringify(platformContent.text);
    }
    
    return JSON.stringify(platformContent);
  }
  
  return typeof content === 'object' ? JSON.stringify(content) : String(content);
};

export default SocialMediaPostPreview;